import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { Worker, HolidayStatus } from '@/lib/models/worker';
import type { WorkerIntegration } from '@/lib/models/workerIntegration';
// TODO: import type { JobOrder } from '@/lib/models/jobOrder'; // Quando JobOrder sarà migrato
// TODO: import type { WorkerHolidayPerYear } from '@/lib/models/workerHolidayPerYear'; // Usare endpoints dedicati
import type { WorkingRelationship } from '@/lib/models/workingRelationship';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per gli endpoint Worker
 * Migrazione incrementale: iniziamo con CRUD standard + alcuni endpoint speciali
 */
interface WorkerEndpoints {
	/**
	 * Recupera lista paginata di lavoratori con query OData
	 * @param query - Query string OData (senza il '?' iniziale)
	 * @returns Promise con FetchResponse contenente ODataResponse di Worker
	 */
	getWorkers(query: string): Promise<FetchResponse<ODataResponse<Worker>>>;

	/**
	 * Recupera un singolo lavoratore per ID
	 * @param id - ID del lavoratore
	 * @returns Promise con FetchResponse contenente Worker
	 */
	getWorker(id: number): Promise<FetchResponse<Worker>>;

	/**
	 * Crea un nuovo lavoratore
	 * @param payload - Dati del lavoratore da creare
	 * @returns Promise con FetchResponse contenente Worker creato
	 */
	postWorker(payload: Partial<Worker>): Promise<FetchResponse<Worker>>;

	/**
	 * Aggiorna un lavoratore esistente
	 * @param id - ID del lavoratore
	 * @param payload - Dati da aggiornare
	 * @returns Promise con FetchResponse contenente Worker aggiornato
	 */
	updateWorker(id: number, payload: Partial<Worker>): Promise<FetchResponse<Worker>>;

	/**
	 * Elimina un lavoratore
	 * @param id - ID del lavoratore da eliminare
	 * @returns Promise con FetchResponse vuoto
	 */
	deleteWorker(id: number): Promise<FetchResponse<void>>;

	// Endpoint speciali - migrazione graduale
	/**
	 * Recupera gli ordini gestiti dal lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con FetchResponse contenente ODataResponse di JobOrder
	 */
	getManagedOrders(workerId: number): Promise<FetchResponse<ODataResponse<any>>>;

	/**
	 * Recupera gli ordini su cui può lavorare
	 * @param workerId - ID del lavoratore
	 * @returns Promise con FetchResponse contenente ODataResponse di JobOrder
	 */
	getWorkedOrders(workerId: number): Promise<FetchResponse<ODataResponse<any>>>;

	/**
	 * Recupera le ferie del lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con FetchResponse contenente ODataResponse di WorkerHolidayPerYear
	 */
	getHolidays(workerId: number): Promise<FetchResponse<ODataResponse<any>>>;

	/**
	 * Recupera i rapporti di lavoro
	 * @param workerId - ID del lavoratore
	 * @returns Promise con FetchResponse contenente ODataResponse di WorkingRelationship
	 */
	getRelationships(workerId: number): Promise<FetchResponse<ODataResponse<WorkingRelationship>>>;

	/**
	 * Recupera lo status delle ferie per anno
	 * @param workerId - ID del lavoratore
	 * @param year - Anno di riferimento
	 * @returns Promise con FetchResponse contenente HolidayStatus
	 */
	getHolidayStatus(workerId: number, year: number): Promise<FetchResponse<HolidayStatus>>;

	/**
	 * Recupera le integrazioni del lavoratore
	 * @param workerId - ID del lavoratore
	 * @returns Promise con FetchResponse contenente ODataResponse di WorkerIntegration
	 */
	getIntegrations(workerId: number): Promise<FetchResponse<ODataResponse<WorkerIntegration>>>;

	/**
	 * Genera export Excel dei lavoratori
	 * @param query - Query string per il filtro
	 * @returns Promise con FetchResponse contenente Blob (file Excel)
	 */
	generateExcel(query: string): Promise<FetchResponse<Blob>>;

	// TODO: Aggiungere gradualmente gli altri endpoint speciali se presenti
}

/**
 * Implementazione degli endpoint Worker
 */
const workerEndpoints: WorkerEndpoints = {
	getWorkers(query: string): Promise<FetchResponse<ODataResponse<Worker>>> {
		// Rimuove il '?' iniziale se presente
		const cleanQuery = query.startsWith('?') ? query.substring(1) : query;
		return api.post('/odata/v1/Workers/$query', cleanQuery, {
			headers: {
				'Content-Type': 'text/plain',
			},
		});
	},

	getWorker(id: number): Promise<FetchResponse<Worker>> {
		return api.get(`/odata/v1/Workers/${id}?$expand=user`);
	},

	postWorker(payload: Partial<Worker>): Promise<FetchResponse<Worker>> {
		return api.post('/api/v1/Workers', payload);
	},

	updateWorker(id: number, payload: Partial<Worker>): Promise<FetchResponse<Worker>> {
		return api.put(`/api/v1/Workers/${id}`, payload);
	},

	deleteWorker(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/Workers/${id}`);
	},

	getManagedOrders(workerId: number): Promise<FetchResponse<ODataResponse<any>>> {
		const now = new Date().toISOString();
		return api.get(
			`/odata/v1/JobOrderManagers?$expand=jobOrder($expand=customer)&$filter=(workerId eq ${workerId}) and ((jobOrder/endDate gt ${now}) or (jobOrder/endDate eq null))`,
		);
	},

	getWorkedOrders(workerId: number): Promise<FetchResponse<ODataResponse<any>>> {
		const now = new Date().toISOString();
		return api.get(
			`/odata/v1/JobOrderAllowedWorkers?$expand=jobOrder($expand=customer)&$filter=(workerId eq ${workerId}) and ((jobOrder/endDate gt ${now}) or (jobOrder/endDate eq null))`,
		);
	},

	getHolidays(workerId: number): Promise<FetchResponse<ODataResponse<any>>> {
		return api.get(`/odata/v1/WorkerHolidayPerYears?$filter=(workerId eq ${workerId})`);
	},

	getRelationships(workerId: number): Promise<FetchResponse<ODataResponse<WorkingRelationship>>> {
		return api.get(
			`/odata/v1/WorkingRelationships?$expand=costCenter&$filter=(workerId eq ${workerId})`,
		);
	},

	getHolidayStatus(workerId: number, year: number): Promise<FetchResponse<HolidayStatus>> {
		return api.get(`/api/v1/Workers/actions/getHolidayStatus/${workerId}/${year}`);
	},

	getIntegrations(workerId: number): Promise<FetchResponse<ODataResponse<WorkerIntegration>>> {
		return api.get(`/odata/v1/WorkerIntegrations?$filter=(sourceId eq ${workerId})`);
	},

	generateExcel(query: string): Promise<FetchResponse<Blob>> {
		return api.get(`/api/v1/Workers/actions/generateExcel/${query}`, {
			responseType: 'blob',
		});
	},
};

export default workerEndpoints;
